import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton } from "@mui/material";
import { createPortal } from "react-dom";

import { EXPIRY_MS, WARNING_THRESHOLD_MS } from "../constants";
import { useSessionTimer } from "../hooks/useSessionTimer";
import { useTabDrag } from "../hooks/useTabDrag";
import type { ImpersonationSession } from "../types";

const TAB_HEIGHT = 28;

interface SessionTabProps {
  session: ImpersonationSession;
  isActive: boolean;
  onSelect: () => void;
  onRefresh: () => void;
  onPopOut: () => void;
  onClose: () => void;
  onDragToSplit: (side: "left" | "right") => void;
  onDragOut: () => void;
  onExitSplit?: () => void;
  onDragStateChange: (isDragging: boolean, dropTarget: "left" | "right" | "detach" | null) => void;
  dropZoneRect: { x: number; y: number; width: number; height: number } | null;
  splitSide?: "left" | "right";
}

export function SessionTab({
  session,
  isActive,
  onSelect,
  onRefresh,
  onPopOut,
  onClose,
  onDragToSplit,
  onDragOut,
  onExitSplit,
  onDragStateChange,
  dropZoneRect,
  splitSide,
}: SessionTabProps) {
  const elapsed = useSessionTimer(session.startedAt);
  const remainingMs = session.expiresAt ? session.expiresAt - Date.now() : EXPIRY_MS - elapsed;
  const isExpired = remainingMs <= 0;
  const isWarning = !isExpired && remainingMs <= WARNING_THRESHOLD_MS;
  const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60_000));

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isDragging,
    dropTarget,
    dragPosition,
  } = useTabDrag({
    onSelect,
    onDragToSplit,
    onDragOut,
    onDragStateChange,
    dropZoneRect,
    isInSplit: Boolean(splitSide),
    onExitSplit,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getTabBackgroundColor(theme: any): string {
    if (isExpired) {
      return theme.palette.error.main;
    }

    if (isWarning) {
      return theme.palette.warning.main;
    }

    if (isActive) {
      return theme.palette.primary.main;
    }

    return theme.palette.grey[600];
  }

  function getSplitBorderStyle(): Record<string, string> {
    if (!splitSide) {
      return {};
    }

    const borderSide = splitSide === "left" ? "borderLeft" : "borderRight";
    return { [borderSide]: "3px solid rgba(255,255,255,0.7)" };
  }

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        height: TAB_HEIGHT,
        paddingX: 1,
        gap: 0.5,
        cursor: isDragging ? "grabbing" : "pointer",
        backgroundColor: getTabBackgroundColor(theme),
        color: theme.palette.common.white,
        borderRadius: "6px 6px 0 0",
        minWidth: 120,
        maxWidth: 200,
        userSelect: "none",
        flexShrink: 0,
        transition: "background-color 0.2s",
        opacity: isDragging ? 0.6 : 1,
        position: "relative",
        ...getSplitBorderStyle(),
        "&:hover": {
          backgroundColor: (() => {
            if (isExpired) {
              return theme.palette.error.dark;
            }

            if (isWarning) {
              return theme.palette.warning.dark;
            }

            return isActive ? theme.palette.primary.dark : theme.palette.grey[700];
          })(),
        },
      })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {splitSide && (
        <Box
          component="span"
          sx={{
            fontSize: "0.55rem",
            fontWeight: 700,
            opacity: 0.8,
            lineHeight: 1,
            marginRight: 0.25,
          }}
        >
          {splitSide === "left" ? "L" : "R"}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: 600,
          fontSize: "0.7rem",
          pointerEvents: "none",
        }}
      >
        {session.targetName} ({remainingMinutes}m)
      </Box>
      <IconButton
        size="small"
        aria-label={`Refresh ${session.targetName}`}
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.125 })}
        onClick={(event) => {
          event.stopPropagation();
          onRefresh();
        }}
      >
        <RefreshIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label={`Pop out ${session.targetName}`}
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.125 })}
        onClick={(event) => {
          event.stopPropagation();
          onPopOut();
        }}
      >
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label={`Close ${session.targetName}`}
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.125 })}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Drag ghost — floating label following cursor */}
      {isDragging &&
        dragPosition &&
        createPortal(
          <Box
            sx={{
              position: "fixed",
              left: dragPosition.x + 12,
              top: dragPosition.y + 12,
              paddingX: 1.5,
              paddingY: 0.5,
              backgroundColor: dropTarget === "detach" ? "grey.700" : "primary.main",
              color: "common.white",
              borderRadius: 1,
              fontSize: "0.75rem",
              fontWeight: 600,
              pointerEvents: "none",
              zIndex: 99999,
              boxShadow: 8,
              whiteSpace: "nowrap",
              opacity: 0.9,
            }}
          >
            {dropTarget === "left" && `◀ Snap left: ${session.targetName}`}
            {dropTarget === "right" && `Snap right: ${session.targetName} ▶`}
            {dropTarget === "detach" && `↗ Pop out: ${session.targetName}`}
            {!dropTarget && session.targetName}
          </Box>,
          document.body
        )}
    </Box>
  );
}
